// features/premium/SubscribedView.tsx — subscribed state: hero + 4 subtabs.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Tabs, ConsolePanel, Button } from '@/ui';
import { Icon } from '@/icons';
import { usePremiumSubscribed, useBilling } from '@/hooks';
import { media } from '@/theme/media';
import { SUBTABS, BENEFIT_ICONS, HUB_BENEFIT_TITLE, isProActiveBenefit } from './meta';
import type { SubtabId } from './meta';

export interface SubscribedViewProps {
  displayName: string;
  /** Opens the Stripe billing portal (or falls back to a toast when
   * unavailable) — see PremiumPage.tsx's handleManageBilling. */
  onManageBilling: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Not set';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'Not set';
  }
}

const Hero = styled.div`
  padding: 24px; margin-bottom: 16px; border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;
const HeroTitle = styled.h2`
  margin: 0 0 6px; font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl}; color: ${({ theme }) => theme.color.textPrimary};
`;
const HeroSub = styled.p`margin: 0; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted};`;
const Body = styled.div`margin-top: 16px;`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media (max-width: 900px){ grid-template-columns: 1fr 1fr; }
  ${media.mobile`grid-template-columns: 1fr;`}
`;
const BenefitCard = styled(ConsolePanel)`display: flex; flex-direction: column; gap: 6px;`;
const BenefitTitle = styled.div`display: flex; align-items: center; gap: 8px; font-weight: 700; color: ${({ theme }) => theme.color.textPrimary}; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};`;
const BenefitBlurb = styled.div`font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textMuted};`;
const Chips = styled.div`display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px;`;
const Chip = styled.span`display: inline-flex; align-items: center; gap: 6px; padding: 5px 10px; border-radius: ${({ theme }) => theme.radius.md}; border: 1px solid ${({ theme }) => theme.color.glassBorder}; font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textPrimary};`;
const Facts = styled.dl`display: grid; grid-template-columns: auto 1fr; gap: 6px 16px; margin: 0 0 16px; & dt { color: ${({ theme }) => theme.color.textMuted}; font-size: ${({ theme }) => theme.fontSize.sm}; } & dd { margin: 0; color: ${({ theme }) => theme.color.textPrimary}; font-size: ${({ theme }) => theme.fontSize.sm}; }`;
const SettingRow = styled.div`display: flex; flex-direction: column; gap: 4px; padding: 10px 0; border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder}; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textPrimary};`;
const SettingHint = styled.span`font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textMuted}; font-weight: 400;`;
const GroupLabel = styled.h3`
  margin: 18px 0 8px; font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textPrimary};
  &:first-child { margin-top: 0; }
`;
const GroupNote = styled.p`
  margin: 0 0 10px; font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textMuted};
`;
const SoonCard = styled(BenefitCard)`opacity: 0.72;`;
const SoonTag = styled.span`
  margin-left: auto; padding: 2px 8px; border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-size: ${({ theme }) => theme.fontSize.xs}; font-weight: 600;
  color: ${({ theme }) => theme.color.textMuted};
`;
const HubCta = styled(Link)`
  display: inline-block; margin-top: 16px; padding: 9px 16px; border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.accentCyan};
  color: ${({ theme }) => theme.color.onAccent}; text-decoration: none; font-family: ${({ theme }) => theme.font.body}; font-weight: 600; font-size: ${({ theme }) => theme.fontSize.sm};
`;

export function SubscribedView({ displayName, onManageBilling }: SubscribedViewProps) {
  const { ultraBenefits } = usePremiumSubscribed();
  const { billing } = useBilling();
  const [active, setActive] = useState<SubtabId>('overview');

  // Fix-round F10: a subscriber sees only what they bought. This view used to
  // render ALL nine seeded benefits under "Your premium benefits are active",
  // including Paid Subscriptions, Paywalled Posts, API & Integrations and the
  // Creators' Hub — every one of them unbuilt and correctly marked
  // non-purchasable on the plans page. The split is declared once in meta.ts.
  const activeBenefits = ultraBenefits.filter((b) => isProActiveBenefit(b.title));
  const soonBenefits = ultraBenefits.filter((b) => !isProActiveBenefit(b.title));
  // The Creators' Hub CTA is gated on the Hub actually being delivered to Pro
  // (its content is still a local seed, so today it is not). It rendered
  // unconditionally before, pointing a subscriber at something they have no
  // access to. When the Hub ships, add it to PRO_ACTIVE_BENEFIT_TITLES.
  const hasHubAccess = isProActiveBenefit(HUB_BENEFIT_TITLE);

  return (
    <div>
      <Hero>
        <HeroTitle>You&apos;re all set, {displayName}!</HeroTitle>
        <HeroSub>Your premium benefits are active. Thanks for supporting free speech.</HeroSub>
      </Hero>
      <Tabs
        items={SUBTABS.map((s) => ({ value: s.id, label: s.label }))}
        value={active}
        onChange={(v) => setActive(v as SubtabId)}
      />
      <Body>
        {active === 'overview' && (
          <Chips>
            {activeBenefits.map((b) => (
              <Chip key={b.title}><Icon name={BENEFIT_ICONS[b.title] ?? 'verify'} size={14} />{b.title}</Chip>
            ))}
          </Chips>
        )}
        {active === 'benefits' && (
          <>
            <GroupLabel>Active on your account</GroupLabel>
            <Grid>
              {activeBenefits.map((b) => (
                <BenefitCard key={b.title}>
                  <BenefitTitle><Icon name={BENEFIT_ICONS[b.title] ?? 'verify'} size={16} />{b.title}</BenefitTitle>
                  <BenefitBlurb>{b.blurb}</BenefitBlurb>
                </BenefitCard>
              ))}
            </Grid>
            {soonBenefits.length > 0 && (
              <>
                <GroupLabel>Not available yet</GroupLabel>
                <GroupNote>
                  These aren&apos;t built yet and aren&apos;t part of what you&apos;re paying
                  for. They&apos;re listed so you know what&apos;s planned.
                </GroupNote>
                <Grid>
                  {soonBenefits.map((b) => (
                    <SoonCard key={b.title}>
                      <BenefitTitle>
                        <Icon name={BENEFIT_ICONS[b.title] ?? 'verify'} size={16} />{b.title}
                        <SoonTag>Coming soon</SoonTag>
                      </BenefitTitle>
                      <BenefitBlurb>{b.blurb}</BenefitBlurb>
                    </SoonCard>
                  ))}
                </Grid>
              </>
            )}
          </>
        )}
        {active === 'billing' && billing && (
          <ConsolePanel>
            <Facts>
              <dt>Plan</dt><dd>Pro</dd>
              <dt>Status</dt><dd>{billing.status || 'active'}</dd>
              <dt>{billing.cancelAtPeriodEnd ? 'Ends on' : 'Renews on'}</dt>
              <dd>{formatDate(billing.currentPeriodEnd)}</dd>
            </Facts>
            <Button variant="secondary" onClick={onManageBilling}>Manage billing</Button>
          </ConsolePanel>
        )}
        {active === 'settings' && (
          <ConsolePanel>
            <SettingRow>
              Supporter badge on my profile
              <SettingHint>
                Automatic once two-factor authentication is on. There&apos;s no
                separate toggle, so it can never be turned on without it.
              </SettingHint>
            </SettingRow>
            <Button variant="secondary" onClick={onManageBilling}>Manage billing</Button>
          </ConsolePanel>
        )}
      </Body>
      {hasHubAccess && <HubCta to="/creators-hub">Open Creators&apos; Hub</HubCta>}
    </div>
  );
}
